import { injectable, inject } from 'inversify';
import Debug from 'debug';
import { FetchService, FetchServiceSymbol, FetchCommandError } from '@/app/fetch/service';
import { CommandQueue, CommandHandler } from '@/app/shared/command-queue'
import { HunspellAdapter, HunspellDictionaryStore, Result, ok, err } from '@xlearn/common'
import { replaceBasePathInContent } from '../shared/utils/base-path';
import { CancelToken } from '../shared/utils';

const debug = Debug('hunspell-service')

export const HunspellServiceSymbol = Symbol.for('HunspellService')

interface HunspellLanguageDictionary {
  language: string
  files: {
    dic: string
    aff: string
  }
}

@injectable()
export class HunspellService implements HunspellAdapter {

  private queue: CommandQueue;

  private dictionaryStore: HunspellDictionaryStore
  private dictionaryUris: HunspellLanguageDictionary[] = []

  constructor(@inject(FetchServiceSymbol)
              private fetchService: FetchService) {

    this.dictionaryStore = new HunspellDictionaryStore(this.loadBuffer.bind(this))
    this.queue = new CommandQueue('hunspell');
  }

  initialize(uris: HunspellLanguageDictionary[]) {
    this.dictionaryUris = uris
  }

  resolveLanguage(language: string) {
    return this.dictionaryStore.resolveLanguage(language)
  }

  releaseLanguage(language: string) {
    this.queue.queue('prepare-languages', async () => this.dictionaryStore.releaseLanguage(language))
  }

  async prepareLanguages(languages: string[]) {
    await this.queue.queueAndWait('prepare-languages',
                            (cancel) => this.dictionaryStore.prepareLanguages(languages, cancel))
  }

  private async loadBuffer(language: string, extension: 'aff' | 'dic', cancel: CancelToken) {

    debug('loadBuffer', language, extension)

    const dictionaryUri = this.dictionaryUris.find(x => x.language === language)
    if (!dictionaryUri) {
      return err({
        code: 'not-found' as 'not-found',
        details: undefined
      })
    }

    const uri = extension === 'aff' ? dictionaryUri.files.aff : dictionaryUri.files.dic
    debug('downloading dictionary', uri)
    
    const fetchResult = await this.fetchService.query(uri, cancel, 'arraybuffer')
    if (!fetchResult.success) {
      return err({
        code: 'error-loading' as 'error-loading',
        details: fetchResult.error
      })
    }

    debug('loadBuffer result', fetchResult)

    return ok(fetchResult.value)
  }
  
  public start() {
    debug('starting HunspellService');

    this.queue.start();
//    this.queue.enqueueRemote('query-tracking-state', () => this.queryTrackingStateCommand.execute());
  }

  public async stop() {
    debug('stopping HunspellService');

    await this.queue.stop()
  }
}
